import { tr, usePartnerTranslation } from "../lib/language";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  CheckCircle2,
  Mail,
} from "lucide-react";
import { EMAIL_REGEX, validatePassword } from "../lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATES = {
  EMAIL: "email",
  OTP: "otp",
  NEW_PASSWORD: "new_password",
  SUCCESS: "success",
};

export default function ForgotPasswordPage() {
  usePartnerTranslation();
  const [stage, setStage] = useState(STATES.EMAIL);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown > 0) {
      const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [cooldown]);

  const emailError = useMemo(() => {
    if (!touched.email) return "";
    if (!email) return tr("Email is required.");
    if (!EMAIL_REGEX.test(email.trim()))
      return tr("Please enter a valid email address.");
    return "";
  }, [email, touched.email]);

  const pwError = useMemo(() => {
    if (!touched.newPassword) return "";
    return validatePassword(newPassword);
  }, [newPassword, touched.newPassword]);

  const confirmError = useMemo(() => {
    if (!touched.confirmPassword) return "";
    if (!confirmPassword) return tr("Please confirm your password.");
    if (confirmPassword !== newPassword) return tr("Passwords do not match.");
    return "";
  }, [confirmPassword, newPassword, touched.confirmPassword]);

  /* ---------------- handlers ---------------- */
  const requestOtp = async (isResend = false) => {
    setTouched((t) => ({ ...t, email: true }));
    setServerError("");
    if (!email || !EMAIL_REGEX.test(email.trim())) return;

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password/send-otp`, {
        email: email.trim(),
      });
      setStage(STATES.OTP);
      setCooldown(30);
      const devOtp = res.data?.dev_otp;
      toast.success(isResend ? tr("Code resent") : tr("Code sent"), {
        description: devOtp
          ? tr("DEV OTP: {{value0}} — expires in 10 minutes. (No mailer wired yet)", { value0: devOtp })
          : tr("We've sent a 6-digit code to {{value0}}.", { value0: email }),
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "We couldn't send a code. Please try again.";
      setServerError(msg);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setOtpError("");
    if (otp.length !== 6) {
      setOtpError(tr("Enter the 6-digit code."));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/verify-otp`, {
        email: email.trim(),
        otp,
      });
      setStage(STATES.NEW_PASSWORD);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        err?.response?.status === 401 ||
        detail === "Authentication is required."
          ? "Invalid or expired OTP"
          : detail || "Invalid or expired OTP";
      setOtpError(msg);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setTouched((t) => ({ ...t, newPassword: true, confirmPassword: true }));
    setServerError("");
    if (validatePassword(newPassword)) return;
    if (!confirmPassword || confirmPassword !== newPassword) return;

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/reset`, {
        email: email.trim(),
        otp,
        new_password: newPassword,
      });
      setStage(STATES.SUCCESS);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Could not reset your password. Please try again.";
      setServerError(msg);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- render ---------------- */
  return (
    <main
      data-testid="forgot-password-page"
      className="flex flex-1 items-center justify-center px-4 py-10 md:py-16"
    >
      <section className="humrahe-card w-full max-w-[720px] px-6 py-12 md:px-16 md:py-16">
        {stage === STATES.EMAIL && (
          <EmailStage
            email={email}
            setEmail={setEmail}
            emailError={emailError}
            setTouched={setTouched}
            onSubmit={() => requestOtp(false)}
            loading={loading}
            serverError={serverError}
          />
        )}
        {stage === STATES.OTP && (
          <OtpStage
            email={email}
            otp={otp}
            setOtp={setOtp}
            otpError={otpError}
            loading={loading}
            cooldown={cooldown}
            onVerify={verifyOtp}
            onResend={() => requestOtp(true)}
            onBack={() => {
              setStage(STATES.EMAIL);
              setOtp("");
              setOtpError("");
            }}
          />
        )}
        {stage === STATES.NEW_PASSWORD && (
          <NewPasswordStage
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            pwError={pwError}
            confirmError={confirmError}
            setTouched={setTouched}
            showPw={showPw}
            setShowPw={setShowPw}
            showConfirmPw={showConfirmPw}
            setShowConfirmPw={setShowConfirmPw}
            onSubmit={resetPassword}
            loading={loading}
            serverError={serverError}
          />
        )}
        {stage === STATES.SUCCESS && <SuccessStage />}
      </section>
    </main>
  );
}

/* ---------------- Stage: Email ---------------- */
const EmailStage = ({
  email,
  setEmail,
  emailError,
  setTouched,
  onSubmit,
  loading,
  serverError,
}) => {
  usePartnerTranslation();
  const submit = (e) => {
    e.preventDefault();
    setTouched((t) => ({ ...t, email: true }));
    onSubmit();
  };
  return (
    <>
      <header className="mb-10 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#EEF4FF]">
          <Mail className="h-6 w-6 text-[#2476FF]" />
        </div>
        <h1
          data-testid="fp-email-heading"
          className="text-3xl font-bold tracking-tight text-[#0F1115] sm:text-4xl"
        >{tr("Forgot your password?")}</h1>
        <p className="mt-3 text-sm text-[#6B7280] sm:text-base">{tr("Enter the email associated with your partner account and we'll send you a verification code.")}</p>
      </header>

      <form
        noValidate
        onSubmit={submit}
        data-testid="fp-email-form"
        className="mx-auto max-w-[460px] space-y-5"
      >
        <div className="space-y-1.5">
          <div className="relative">
            <label htmlFor="fp-email" className="humrahe-field-label">{tr("Email")}<span className="text-[#E5484D]">*</span>
            </label>
            <input
              id="fp-email"
              type="email"
              autoComplete="email"
              data-testid="fp-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
              placeholder={tr("Enter your Email")}
              className={`humrahe-field ${emailError ? "humrahe-field-error" : ""}`}
            />
          </div>
          {emailError && (
            <p
              data-testid="fp-email-error"
              className="flex items-center gap-1.5 ps-1 text-xs font-medium text-[#E5484D]"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              {emailError}
            </p>
          )}
        </div>

        {serverError && (
          <div
            data-testid="fp-server-error"
            className="flex items-start gap-2 rounded-lg border border-[#FDD8D8] bg-[#FFF4F4] px-3 py-2.5 text-sm text-[#B42318]"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{serverError}</span>
          </div>
        )}

        <button
          type="submit"
          data-testid="fp-send-code-btn"
          disabled={loading}
          className="humrahe-btn-primary mt-2 inline-flex h-12 w-full items-center justify-center rounded-lg text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-70"
        >
          {loading ? (
            <>
              <Loader2 className="me-2 h-4 w-4 animate-spin" />{tr("Sending code…")}</>
          ) : (
            tr("Send verification code")
          )}
        </button>

        <div className="pt-1 text-center">
          <Link
            to="/"
            data-testid="fp-back-to-signin"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F1115] hover:text-[#2476FF]"
          >
            <ArrowLeft className="h-4 w-4" />{tr("Back to sign in")}</Link>
        </div>
      </form>
    </>
  );
};

/* ---------------- Stage: OTP ---------------- */
const OtpStage = ({
  email,
  otp,
  setOtp,
  otpError,
  loading,
  cooldown,
  onVerify,
  onResend,
  onBack,
}) => {
  usePartnerTranslation();
  return (
    <>
      <header className="mb-10 text-center">
        <h1
          data-testid="fp-otp-heading"
          className="text-3xl font-bold tracking-tight text-[#0F1115] sm:text-4xl"
        >{tr("Enter the code")}</h1>
        <p className="mt-3 text-sm text-[#6B7280] sm:text-base">{tr("We've sent a 6-digit verification code to")}{" "}
          <span className="font-semibold text-[#0F1115]">{email}</span>.
        </p>
      </header>

      <div className="mx-auto max-w-[460px] space-y-5">
        <div className="space-y-1.5">
          <input
            data-testid="fp-otp-input"
            inputMode="numeric"
            maxLength={6}
            value={otp}
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            placeholder="••••••"
            className={`humrahe-field !h-14 text-center text-2xl tracking-[0.6em] ${otpError ? "humrahe-field-error" : ""}`}
          />
          {otpError && (
            <p
              data-testid="fp-otp-error"
              className="flex items-center gap-1.5 ps-1 text-xs font-medium text-[#E5484D]"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              {otpError}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={onVerify}
          disabled={loading || otp.length !== 6}
          data-testid="fp-verify-btn"
          className="humrahe-btn-primary inline-flex h-12 w-full items-center justify-center rounded-lg text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-70"
        >
          {loading ? (
            <>
              <Loader2 className="me-2 h-4 w-4 animate-spin" />{tr("Verifying…")}</>
          ) : (
            tr("Verify code")
          )}
        </button>

        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={onBack}
            data-testid="fp-otp-back-btn"
            className="inline-flex items-center gap-1.5 font-medium text-[#0F1115] hover:text-[#2476FF]"
          >
            <ArrowLeft className="h-4 w-4" />{tr("Change email")}</button>
          <button
            type="button"
            onClick={onResend}
            disabled={cooldown > 0 || loading}
            data-testid="fp-otp-resend-btn"
            className="font-semibold text-[#2476FF] transition-colors hover:text-[#1861dc] disabled:text-[#B6BAC2]"
          >
            {cooldown > 0 ? tr("Resend in {{value0}}s", { value0: cooldown }) : tr("Resend code")}
          </button>
        </div>
      </div>
    </>
  );
};

/* ---------------- Stage: New Password ---------------- */
const NewPasswordStage = ({
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  pwError,
  confirmError,
  setTouched,
  showPw,
  setShowPw,
  showConfirmPw,
  setShowConfirmPw,
  onSubmit,
  loading,
  serverError,
}) => {
  usePartnerTranslation();
  return (
    <>
      <header className="mb-10 text-center">
        <h1
          data-testid="fp-new-heading"
          className="text-3xl font-bold tracking-tight text-[#0F1115] sm:text-4xl"
        >{tr("Set a new password")}</h1>
        <p className="mt-3 text-sm text-[#6B7280] sm:text-base">{tr("Choose a strong password you haven't used before on this account.")}</p>
      </header>

      <form
        noValidate
        onSubmit={onSubmit}
        className="mx-auto max-w-[460px] space-y-5"
        data-testid="fp-new-form"
      >
        <PwInput
          id="fp-new-password"
          label={tr("New Password")}
          testid="fp-new-password-input"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, newPassword: true }))}
          visible={showPw}
          onToggle={() => setShowPw((s) => !s)}
          error={pwError}
          helperText="At least 8 characters and include at least 1 number."
        />

        <PwInput
          id="fp-confirm-password"
          label={tr("Confirm Password")}
          testid="fp-confirm-password-input"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
          visible={showConfirmPw}
          onToggle={() => setShowConfirmPw((s) => !s)}
          error={confirmError}
        />

        {serverError && (
          <div
            data-testid="fp-reset-server-error"
            className="flex items-start gap-2 rounded-lg border border-[#FDD8D8] bg-[#FFF4F4] px-3 py-2.5 text-sm text-[#B42318]"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{serverError}</span>
          </div>
        )}

        <button
          type="submit"
          data-testid="fp-reset-btn"
          disabled={loading}
          className="humrahe-btn-primary mt-2 inline-flex h-12 w-full items-center justify-center rounded-lg text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-70"
        >
          {loading ? (
            <>
              <Loader2 className="me-2 h-4 w-4 animate-spin" />{tr("Resetting…")}</>
          ) : (
            tr("Reset password")
          )}
        </button>
      </form>
    </>
  );
};

const PwInput = ({
  id,
  label,
  testid,
  value,
  onChange,
  onBlur,
  visible,
  onToggle,
  error,
  helperText
}) => {
  usePartnerTranslation();
  return <div className="space-y-1.5">
    <div className="relative">
      <label htmlFor={id} className="humrahe-field-label">
        {label}
        <span className="text-[#E5484D]">*</span>
      </label>
      <input id={id} type={visible ? "text" : "password"} autoComplete="new-password" data-testid={testid} value={value} onChange={onChange} onBlur={onBlur} placeholder="" className={`humrahe-field ${error ? "humrahe-field-error" : ""}`} />
      <button type="button" onClick={onToggle} aria-label={visible ? tr("Hide password") : tr("Show password")} className="absolute end-4 top-1/2 -translate-y-1/2 text-[#6B7280] transition-colors hover:text-[#0F1115]">
        {visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </button>
    </div>
    {error ? <p data-testid={`${testid}-error`} className="flex items-center gap-1.5 ps-1 text-xs font-medium text-[#E5484D]">
        <AlertCircle className="h-3.5 w-3.5" />
        {error}
      </p> : helperText ? <p className="ps-1 text-xs text-[#6B7280]">{helperText}</p> : null}
  </div>;
};

/* ---------------- Stage: Success ---------------- */
const SuccessStage = () => {
  usePartnerTranslation();
  const navigate = useNavigate();
  return (
    <div className="text-center" data-testid="fp-success">
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#EAF7EE]">
        <CheckCircle2 className="h-8 w-8 text-[#1F7A3D]" strokeWidth={2.2} />
      </div>
      <h1 className="text-3xl font-bold tracking-tight text-[#0F1115] sm:text-4xl">{tr("Password reset")}</h1>
      <p className="mx-auto mt-3 max-w-md text-sm text-[#6B7280] sm:text-base">{tr("Your password has been updated successfully. You can now sign in with your new credentials.")}</p>
      <button
        type="button"
        onClick={() => navigate("/")}
        data-testid="fp-back-to-signin-success"
        className="humrahe-btn-primary mt-8 inline-flex h-12 min-w-[240px] items-center justify-center rounded-lg px-6 text-sm font-semibold"
      >{tr("Back to sign in")}</button>
    </div>
  );
};
